import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import EmailVerification from './EmailVerification';
import * as authApi from '../api/auth';
import * as usersApi from '../api/users';

function SignUp({ onSignup }) {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState(""); // success or error
    const [showPassword, setShowPassword] = useState(false); // toggle password visibility
    const navigate = useNavigate();
    const toast = useRef(null);

    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;

    // Google Sign-In initialization
    useEffect(() => {
        window.google?.accounts.id.initialize({
            client_id: "728842576259-68rcm61csdn172lmf5d83e1ughdj18l4.apps.googleusercontent.com",
            callback: handleGoogleCallback,
        });
        window.google?.accounts.id.renderButton(
            document.getElementById("googleSignIn"),
            { theme: "outline", size: "large" }
        );
    }, []);

    const handleGoogleCallback = async (response) => {
        const idToken = response.credential;
        try {
            const googleResponse = await authApi.googleLogin(idToken);
            if (googleResponse) {
                const username = googleResponse.user?.username || googleResponse.username || googleResponse.email;
                setMessage("Google sign-in successful! Redirecting...");
                setMessageType("success");
                onSignup(username);
                setTimeout(() => {
                  toast.current?.show({
                    severity: 'success',
                    summary: 'Signup Successful',
                    detail: 'Your account has been created and you are now signed in!',
                    life: 1500
                  });
                  setTimeout(() => {
                    navigate("/tax-insights");
                  }, 1500);
                }, 10);
            } else {
                setMessage("Google sign-in failed. Please try again.");
                setMessageType("error");
                toast.current?.show({
                  severity: 'error',
                  summary: 'Google Sign-In Failed',
                  detail: 'Google sign-in failed. Please try again.',
                  life: 3000
                });
            }
        } catch (error) {
            setMessage("Error during Google sign-in. Please try again.");
            setMessageType("error");
            toast.current?.show({
              severity: 'error',
              summary: 'Google Sign-In Error',
              detail: 'Error during Google sign-in. Please try again.',
              life: 3000
            });
        }
    };

    const handleInputChange = (setter) => (e) => {
        setter(e.target.value);
    };

    const validateEmail = () => {
        if (!emailRegex.test(username)) {
            toast.current.show({
                severity: 'error',
                summary: 'Invalid Email',
                detail: 'Please enter a valid email address',
                life: 3000
            });
            return false;
        }
        return true;
    };

    const handleCreateUser = async () => {
        if (!validateEmail()) {
            return;
        }
        toast.current.show({
            severity: 'info',
            summary: 'Please wait',
            detail: 'Creating your account...',
            life: 2000,
            key: 'signup'
        });
        try {
            // Create the user
            const createData = await usersApi.createUser(username, password);
            {
                const { message, requiresVerification } = createData;
                
                if (requiresVerification) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Account Created',
                        detail: message || 'Please check your email for the verification code',
                        life: 3000,
                        key: 'signup'
                    });
                    // Navigate to verification page with email in state
                    navigate('/verify-email', { state: { email: username } });
                } else {
                    // If verification not required, directly sign in
                    onSignup(username);
                    setTimeout(() => {
                      toast.current?.show({
                        severity: 'success',
                        summary: 'Signup Successful',
                        detail: 'Your account has been created and you are now signed in!',
                        life: 1500,
                        key: 'signup'
                      });
                      setTimeout(() => {
                        navigate('/tax-insights');
                      }, 1500);
                    }, 10);
                }
            }
        } catch (error) {
            const errorData = error.response?.data;
            const errorMessage =
              (typeof errorData === 'string' ? errorData : errorData?.message) ||
              'Error creating account. Please try again.';
            
            console.log('Signup errorMessage:', errorMessage);
            // Special handling for existing user
            if (errorMessage.trim().toLowerCase().includes('username already exists')) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Account Exists',
                    detail: 'This email is already registered. Please log in or verify your email.',
                    life: 1500,
                    key: 'signup'
                });
                setTimeout(() => {
                    navigate('/auth');
                }, 1500);
                return;
            }
            toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: errorMessage,
                    life: 3000,
                    key: 'signup'
                });
        }
    };

    const handleVerificationComplete = async (verifiedEmail) => {
        try {
            // First check if the user exists and is verified
            {
                // Email verified by EmailVerification component — just sign in
                onSignup(verifiedEmail);
                navigate("/tax-insights");
            }
            if (false) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Email verification failed. Please try again.',
                    life: 3000
                });
            }
        } catch (error) {
            console.error('Error in verification completion:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Something went wrong. Please try again.',
                life: 3000
            });
        }
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handleResendCode = async () => {
        toast.current.show({
            severity: 'info',
            summary: 'Please wait',
            detail: 'Sending verification code...',
            life: 2000
        });
        try {
            // ... your resend code logic here ...
            toast.current.show({
                severity: 'success',
                summary: 'Verification Sent',
                detail: 'A new verification code has been sent to your email.',
                life: 3000
            });
        } catch (error) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to send verification code. Please try again.',
                life: 3000
            });
        }
    };

    return (
        <div style={styles.container}>
            <Toast ref={toast} position="bottom-center" />
            <div style={styles.formCard}>
            <h2 style={styles.title}>Welcome to ProfiTree</h2>

            <div style={styles.formGroup}>
                <label style={styles.label}>Email</label>
                <input
                        type="email"
                    placeholder="me@example.com"
                    value={username}
                    onChange={handleInputChange(setUsername)}
                        onBlur={validateEmail}
                    style={styles.input}
                />
            </div>

            <div style={styles.formGroup}>
                <label style={styles.label}>Password</label>
                <div style={styles.passwordContainer}>
                    <input
                        type={showPassword ? "text" : "password"}
                            placeholder="Enter your password"
                        value={password}
                        onChange={handleInputChange(setPassword)}
                        style={{ ...styles.input, marginBottom: 0 }}
                    />
                    <button onClick={togglePasswordVisibility} style={styles.showPasswordButton}>
                        {showPassword ? "Hide" : "Show"}
                    </button>
                </div>
            </div>

            <button onClick={handleCreateUser} style={styles.button}>
                Sign Up
            </button>

                <div style={styles.divider}>
                    <div style={styles.dividerLine}></div>
                    <span style={styles.dividerText}>or continue with</span>
                    <div style={styles.dividerLine}></div>
                </div>

                <div id="googleSignIn" style={styles.googleButton}></div>

            <div style={styles.signUpText}>
                Already a client? <a href="/auth" style={styles.signUpLink}>Sign in</a>
                </div>
            </div>
        </div>
    );
}

const styles = {
    container: {
        minHeight: 'calc(100vh - 80px)', // Subtract header height
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        backgroundColor: '#f8f9fa',
    },
    formCard: {
        width: '100%',
        maxWidth: '400px',
        padding: '32px',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
        margin: '20px auto', // Reduced from 40px
    },
    title: {
        fontSize: '28px',
        color: '#264653',
        marginBottom: '30px',
        fontWeight: '600',
        textAlign: 'center',
    },
    formGroup: {
        marginBottom: '20px',
    },
    label: {
        display: 'block',
        fontSize: '15px',
        color: '#264653',
        marginBottom: '8px',
        fontWeight: '500',
    },
    input: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '15px',
        borderRadius: '8px',
        border: '2px solid #e0e0e0',
        transition: 'all 0.2s ease',
        '&:focus': {
            borderColor: '#2a9d8f',
            outline: 'none',
            boxShadow: '0 0 0 3px rgba(42, 157, 143, 0.1)',
        },
    },
    passwordContainer: {
        position: 'relative',
    },
    showPasswordButton: {
        position: 'absolute',
        right: '12px',
        top: '50%',
        transform: 'translateY(-50%)',
        background: 'none',
        border: 'none',
        color: '#2a9d8f',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500',
        padding: '4px 8px',
        borderRadius: '4px',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: 'rgba(42, 157, 143, 0.1)',
        },
    },
    button: {
        width: '100%',
        padding: '14px',
        fontSize: '16px',
        backgroundColor: '#264653',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontWeight: '600',
        transition: 'all 0.2s ease',
        marginTop: '10px',
        '&:hover': {
            backgroundColor: '#1a323d',
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        },
    },
    divider: {
        display: 'flex',
        alignItems: 'center',
        margin: '24px 0',
        gap: '15px',
    },
    dividerLine: {
        flex: 1,
        height: '1px',
        backgroundColor: '#e0e0e0',
    },
    dividerText: {
        color: '#666',
        fontSize: '14px',
        whiteSpace: 'nowrap',
    },
    googleButton: {
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '20px',
    },
    signUpText: {
        fontSize: '15px',
        color: '#264653',
        textAlign: 'center',
    },
    signUpLink: {
        color: '#2a9d8f',
        textDecoration: 'none',
        fontWeight: '600',
        transition: 'color 0.2s ease',
        '&:hover': {
            color: '#1f7268',
        },
    },
};

export default SignUp;
